import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { toast } from '@/components/ui/Toast';
import { agentApi, type DialerState } from '@/shared/api/agentApi';

/**
 * DialerBar — "תותח השיחות": סרגל קבוע בראש אזור התוכן של הנציג, שנראה בכל
 * מסך במערכת. מציג את **הקמפיינים שלו**, מי מחויג עכשיו, ונותן פעולות —
 * הפעלה/השהיה פר-קמפיין ומעבר למסך התותח.
 *
 * 07/09/2026 (דורון: "תותח שיחות הכוונה זה לראות את הקמפיינים שלו"): הסרגל
 * מוצג בכל פעם שיש לנציג קמפיין משויך, גם כשהחיוג האוטומטי כבוי — קודם הוא
 * נעלם לגמרי כשאף קמפיין לא היה במצב auto, והנציג לא ראה לאילו קמפיינים
 * הוא משויך בכלל. קמפיין ידני (preview) מקבל כפתור "התחל חיוג" שמוביל למסך
 * העבודה. polling קליל כל 5 שניות.
 */
export function DialerBar() {
  const [state, setState] = useState<DialerState | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  // עצירה דורשת סיבה (אפיון 5.3) — השרת מחזיר 422 בלעדיה. הקמפיין שנבחר
  // לעצירה ממתין כאן עד שהנציג יבחר סיבה.
  const [pausing, setPausing] = useState<number | null>(null);
  const [reasons, setReasons] = useState<Array<{ code: string; label: string }>>([]);
  const navigate = useNavigate();
  const alive = useRef(true);

  const poll = useCallback(() => {
    agentApi.dialerState()
      .then((s) => { if (alive.current) setState(s); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    alive.current = true;
    poll();
    const t = setInterval(poll, 5000);
    return () => { alive.current = false; clearInterval(t); };
  }, [poll]);

  const campaigns = state?.campaigns ?? [];
  // אין קמפיינים בכלל ⇒ אין מה להציג (נציג שלא משויך לשום קמפיין).
  if (campaigns.length === 0) return null;

  const autoCampaigns = campaigns.filter((c) => c.auto);
  const call = state?.active_call ?? null;
  const anyActive = autoCampaigns.some((c) => !c.paused && c.in_window);

  /** חידוש הוא מיידי; עצירה פותחת תחילה את בורר הסיבה. */
  async function togglePause(campaignId: number, paused: boolean) {
    if (paused) {
      setPausing(campaignId);
      if (reasons.length === 0) {
        agentApi.dialerPauseReasons().then(setReasons).catch(() => setReasons([]));
      }
      return;
    }
    await applyPause(campaignId, false);
  }

  async function applyPause(campaignId: number, paused: boolean, reason?: string) {
    setBusy(campaignId);
    try {
      await agentApi.dialerPause(campaignId, paused, reason);
      toast(paused ? 'החיוג האוטומטי הושהה עבורך' : 'החיוג האוטומטי חודש', 'ok');
      setPausing(null);
      poll();
    } catch {
      toast('שגיאה בעדכון', 'danger');
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
    {/* בורר סיבת עצירה — הסיבה נרשמת בלוג לצורך דוחות (אפיון 5.3). */}
    {pausing !== null && (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="סיבת עצירת החיוג"
        style={{
          position: 'fixed', inset: 0, zIndex: 1400,
          background: 'oklch(0% 0 0 / 0.45)', display: 'grid', placeItems: 'center', padding: 16,
        }}
        onClick={() => setPausing(null)}
      >
        <div
          className="stack"
          onClick={(e) => e.stopPropagation()}
          style={{ background: 'var(--surface)', borderRadius: 'var(--r-3)', border: '1px solid var(--line)', padding: 'var(--pad-card)', minWidth: 280, gap: 10 }}
        >
          <strong style={{ fontSize: 'var(--t-md)' }}>למה אתה עוצר?</strong>
          <p style={{ margin: 0, fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
            הסיבה נרשמת לצורך דוחות.
          </p>
          {reasons.length === 0 && <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>טוען סיבות…</span>}
          {reasons.map((r) => (
            <button
              key={r.code}
              type="button"
              className="btn"
              disabled={busy === pausing}
              onClick={() => void applyPause(pausing, true, r.code)}
            >
              {r.label}
            </button>
          ))}
          <button type="button" className="btn ghost" onClick={() => setPausing(null)}>ביטול</button>
        </div>
      </div>
    )}
    <div
      role="status"
      style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '8px 16px',
        background: anyActive ? 'var(--accent-soft, var(--surface-2))' : 'var(--surface-2)',
        borderBottom: `1px solid ${anyActive ? 'var(--accent)' : 'var(--line)'}`,
        fontSize: 'var(--t-sm)',
      }}
    >
      {/* מצב אחד ברור בקצה, ואז הקמפיינים. קודם הופיעו כאן שתי כותרות
          ("תותח שיחות פעיל" + שם הקמפיין) וכפתור נפרד לכל דבר. */}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontWeight: 600, color: anyActive ? 'var(--accent-ink)' : 'var(--text-2)' }}>
        <span
          aria-hidden="true"
          style={{
            width: 8, height: 8, borderRadius: '50%',
            background: anyActive ? 'var(--ok)' : 'var(--faint)',
            boxShadow: anyActive ? '0 0 0 3px var(--ok-soft)' : undefined,
          }}
        />
        {anyActive ? 'מחייג' : autoCampaigns.length > 0 ? 'החיוג מושהה' : 'הקמפיינים שלי'}
      </span>

      {campaigns.map((c) => (
        <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '3px 4px 3px 10px', borderRadius: 999, background: 'var(--surface)', border: '1px solid var(--line)' }}>
          <span style={{ fontWeight: 500 }}>{c.name}</span>
          {c.auto && !c.in_window && !c.paused && (
            <span style={{ color: 'var(--warn)', fontSize: 'var(--t-xs)' }}>מחוץ לשעות</span>
          )}
          {c.auto ? (
            <button
              type="button"
              className="btn ghost"
              style={{ height: 24, padding: '0 10px', fontSize: 'var(--t-xs)', borderRadius: 999, color: c.paused ? 'var(--ok)' : 'var(--text-2)' }}
              disabled={busy === c.id}
              onClick={() => void togglePause(c.id, !c.paused)}
            >
              <Icon name={c.paused ? 'play' : 'pause'} size={12} />
              {c.paused ? 'המשך' : 'השהה'}
            </button>
          ) : (
            /* קמפיין ידני (preview): אין מה להשהות — הנציג מתחיל אותו במסך העבודה. */
            <button
              type="button"
              className="btn ghost"
              style={{ height: 24, padding: '0 10px', fontSize: 'var(--t-xs)', borderRadius: 999, color: 'var(--accent)' }}
              onClick={() => void navigate(`/agent/campaign?campaign=${c.id}`)}
            >
              <Icon name="phoneOut" size={12} /> התחל
            </button>
          )}
        </span>
      ))}

      {call && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-2)' }}>
          <Icon name="phoneOut" size={14} color="var(--accent)" />
          <strong>{call.lead_name || 'מחייג'}</strong>
          <span dir="ltr" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)' }}>{call.to_number}</span>
        </span>
      )}

      <span style={{ flex: 1 }} />
      <button
        type="button"
        className="btn ghost"
        style={{ height: 26, padding: '0 10px', fontSize: 'var(--t-xs)' }}
        onClick={() => void navigate('/agent/campaigns')}
      >
        לכל הקמפיינים
        <Icon name="arrowL" size={13} />
      </button>
    </div>
    </>
  );
}
